<template>
  <EmptySubscriptionPage
    v-if="!hasFeatureFlag('module-register')"
    :text="{
      title: $t('upgrade_subscription_page.register.day_overview.title'),
      description: $t(
        'upgrade_subscription_page.register.day_overview.description'
      ),
      btnPrimary: $t('upgrade_subscription_page.upgrade_btn')
    }"
    imageName="day-overview"
  />

  <Flex
    v-else
    direction="column"
  >
    <router-view />
    <Flex>
      <BaseDatePicker
        v-model="date"
        onlyPastDates
        arrows
      />
      <BaseButton
        color="inverted"
        :disabled="showingToday"
        @click="setCurrentDate"
      >
        {{ $t('global.today') }}
      </BaseButton>
    </Flex>
    <BaseCard v-show="orders && orders.length">
      <div :class="$style.tableWrap">
        <div :class="$style.table">
          <BaseTableRow head>
            <BaseTableCell :width="10">{{ $t('global.number') }}</BaseTableCell>
            <BaseTableCell :width="10">{{ $t('global.time') }}</BaseTableCell>
            <BaseTableCell :width="10">
              {{ $t('global.items.employee', 2) }}
            </BaseTableCell>
            <BaseTableCell :width="10">
              {{ $t('global.items.customer', 1) }}
            </BaseTableCell>
            <BaseTableCell :width="45">
              {{ $t('global.items.item', 2) }}
            </BaseTableCell>
            <BaseTableCell :width="15">{{ $t('global.status') }}</BaseTableCell>
          </BaseTableRow>
          <BaseTableRow
            v-for="(order, index) in orders"
            :key="`row-${index}`"
            :routerLink="{ name: 'day-overview-item', params: { id: order.id } }"
            v-test="'baseTableRow'"
          >
            <BaseTableCell :width="10">{{ order.number }}</BaseTableCell>
            <BaseTableCell :width="10">
              {{ filters.time(order.invoicedAt) }}
            </BaseTableCell>
            <BaseTableCell :width="10">{{ order.employeeNames }}</BaseTableCell>
            <BaseTableCell :width="10">
              {{ order.customer ? order.customer.fullName : '-' }}
            </BaseTableCell>
            <BaseTableCell :width="45">
              <BaseTableRow
                v-for="(item, index) in order.items"
                :key="`item-sub-${index}`"
                small
              >
                <BaseTableCell>{{ item.description }}</BaseTableCell>
                <BaseTableCell>{{ item.quantity }}</BaseTableCell>
                <BaseTableCell
                  >{{ filters.currency(item.price) }}</BaseTableCell
                >
                <BaseTableCell>
                  {{ filters.currency(item.quantity * item.price) }}
                </BaseTableCell>
              </BaseTableRow>
              <BaseTableRow
                v-for="(transaction, index) in order.transactions"
                :key="`transaction-${index}`"
                small
                footer
              >
                <BaseTableCell />
                <BaseTableCell />
                <BaseTableCell>
                  {{ $t(`global.payments.${transaction.type.toLowerCase()}`, brandNamePay()) }}
                </BaseTableCell>
                <BaseTableCell>
                  {{ filters.currency(transaction.amount) }}
                </BaseTableCell>
              </BaseTableRow>
            </BaseTableCell>
            <BaseTableCell :width="15">
              <BaseLabel :state="order.state.toLowerCase()" />
            </BaseTableCell>
          </BaseTableRow>
          <Pagination
            :items="orders"
            :loading="$apollo.loading"
            fieldName="orders"
            :apolloQuery="$apollo.queries.orders"
            :extraVariables="{
              date: date,
              withoutDrafts: true,
              locationId,
              dataScope
            }"
          />
        </div>
      </div>
    </BaseCard>
    <EmptyPageContent
      v-show="noResults"
      imageName="day-overview"
    />
  </Flex>
</template>

<script lang="ts">
import gql from 'graphql-tag';
import { mapState } from 'pinia';
import { defineComponent } from 'vue';
import BaseTableCell from '@/components/_deprecated/BaseTableCell.vue';
import BaseTableRow from '@/components/_deprecated/BaseTableRow.vue';
import Pagination from '@/components/_deprecated/Pagination.vue';
import EmptyPageContent from '@/components/EmptyPageContent.vue';
import dayjs from '@/dayjs';
import filters from '@/filters';
import { brandNamePay } from '@/helpers/brand-name';
import EmptySubscriptionPage from '@/modules/_shared/subscription/EmptySubscriptionPage.vue';
import { useLocationsStore } from '@/stores/locations';
import { useUserStore } from '@/stores/user';

export default defineComponent({
  components: {
    Pagination,
    EmptyPageContent,
    BaseTableRow,
    BaseTableCell,
    EmptySubscriptionPage,
  },
  setup() {
    return {
      filters,
      brandNamePay,
    };
  },
  data() {
    return {
      date: null,
    };
  },
  apollo: {
    orders: {
      query: gql`
        query getOrders(
          $withoutDrafts: Boolean
          $date: ISO8601Date
          $pagination: PaginationAttributes
          $locationId: ID
          $dataScope: DataScope
        ) {
          orders(
            withoutDrafts: $withoutDrafts
            date: $date
            pagination: $pagination
            locationId: $locationId
            dataScope: $dataScope
          ) {
            id
            state
            number
            invoicedAt
            customer {
              fullName
            }
            items {
              price
              quantity
              description
            }
            employeeNames
            transactions {
              type
              amount
            }
          }
        }
      `,
      variables() {
        return {
          withoutDrafts: true,
          date: this.date,
          pagination: {
            currentPage: 1,
            perPage: 25,
          },
          locationId: this.locationId,
          dataScope: this.dataScope,
        };
      },
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
    },
  },
  computed: {
    ...mapState(useUserStore, ['hasFeatureFlag']),
    ...mapState(useLocationsStore, ['locationId', 'dataScope']),
    showingToday() {
      return dayjs(this.date).isToday();
    },
    noResults() {
      return !this.$apollo.loading && this.orders && !this.orders.length;
    },
  },
  methods: {
    setCurrentDate() {
      this.date = dayjs().tz().format('YYYY-MM-DD');
    },
  },
  created() {
    this.setCurrentDate();
  },
});
</script>

<style
  lang="scss"
  module
>
.tableWrap {
  overflow-x: auto;
}

.table {
  min-width: 1050px;
}
</style>
